import BigNumber from "bignumber.js";
import constants from "../constants";
import moment from "moment";
import React from "react";
import xlsx from "xlsx";
import store from "../store";

export function uuidv4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        // eslint-disable-next-line no-mixed-operators
        const r = Math.random() * 16 | 0, v = c === "x" ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

export function balanceToUsd(balance, type = "balance") {
    return +(new BigNumber(balance[type]).multipliedBy(balance.usdPrice).toFixed(2));
}

export function balancesToUsdTotal(balances, type = "balance", exclude = []) {
    return (balances && balances.length) ? BigNumber.sum(...balances.map(b => exclude.includes(b.currency) ? 0 : balanceToUsd(b, type))).toNumber() : 0;
}

export function supportedTokens() {
    const env = store.getState().Variables?.env;
    const tokens = store.getState().Tokens.list;
    const supportedTokens = (env?.SUPPORTED_TOKENS || "USD,BTC,ETH").split(",");
    return Object.values(tokens).filter(t => supportedTokens.some(st => st.toLowerCase() === t.symbol.toLowerCase()));
}

export function tradableTokens(custodian) {
    const env = store.getState().Variables?.env;
    const tokens = (custodian && custodian.supportedTokens) ? custodian.supportedTokens : supportedTokens();
    if (env?.TRADABLE_TOKENS && env?.TRADABLE_TOKENS !== "null") {
        return tokens.filter(t => env?.TRADABLE_TOKENS?.split(",").some(st => st.toLowerCase() === t.symbol.toLowerCase()));
    }
    return tokens;
}

export function groupBy(array, fn) {
    return array.reduce((acc, item) => {
      const key = fn(item);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
}

export function filterSupportedBalances(balances = []) {
    const balanceMap = groupBy(balances, (b) => b.currency);
    const viableBalances = Object.keys(balanceMap).reduce((acc, m) => {
        const total = balanceMap[m].reduce((total, b) => total + parseFloat((b.balance || 0)), 0);
        if (total > 0) acc.push(...balanceMap[m]);
        return acc;
    }, []);
    if (viableBalances.length) return viableBalances;
    return [];
}

export function filterSupportedCustodians(custodians) {
    if (!custodians || custodians.length === 0) {
        return [];
    }
    const tokens = store.getState().Tokens.list;
    return custodians.map(custodian => {
        custodian.supportedTokens = custodian.supportedTokens?.length ? custodian.supportedTokens.filter(st => tokens[st]).map(st => tokens[st]) : null;
        if (!store.getState().Variables?.env?.SUPPORTED_CUSTODIANS || store.getState().Variables?.env?.SUPPORTED_CUSTODIANS === "null") {
            return custodian;
        }
        const supportedCustodians = store.getState().Variables?.env?.SUPPORTED_CUSTODIANS.split(",");
        if (!supportedCustodians.some(sc => parseInt(sc) === custodian.id)) {
            custodian.disabled = true;
        }
        return custodian;
    });
}

export function isNumber(value) {
    return typeof value === "number" && isFinite(value);
}

export function floorCurrency(number, currency, fractionDigits) {
    if (currency.toLowerCase() === "usd" && !fractionDigits) {
        return new BigNumber(number).toFixed(2, 3);
    }
    if (isNumber(fractionDigits)) {
        return new BigNumber(number).toFixed(fractionDigits, 3);
    }
    return new BigNumber(number).toString();
}

export function wrapUsd(number, parentheses) {
    if (typeof number === "string") {
        number = parseFloat(number);
    }
    if (isNaN(number)) {
        return <span/>;
    }
    const isNegative = new BigNumber(number).isNegative();
    let str = "";
    if (parentheses) {
        const absValue = Math.abs(number);
        const absStr = formatUsd(absValue);
        str = isNegative ? "(" + absStr + ")" : absStr;
    } else {
        str = formatUsd(number);
    }
    return <span className={isNegative ? "text-danger" : "text-success"}>{str}</span>;
}

export function formatUsd(number, fractionDigits) {
    if (typeof number === "string") {
        number = parseFloat(number);
    }
    if (isNaN(number)) {
        return "";
    }
    return parseFloat(floorCurrency(number, "usd", fractionDigits)).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: fractionDigits || 2,
        maximumFractionDigits: fractionDigits || 2
    });
}

export function formatCurrency(number, currency, toFixed = true, fractionDigits) {
    if (typeof number === "string") {
        number = parseFloat(number);
    }
    if (new BigNumber(number).isNaN()) {
        return "-";
    }
    if (currency.toLowerCase() === "usd") {
        return formatUsd(number);
    }
    if (toFixed) {
        if (!isNumber(fractionDigits)) {
            switch (currency.toLowerCase()) {
                case "eth":
                    fractionDigits = 3;
                    break;
                case "btc":
                    fractionDigits = 4;
                    break;
                default:
                    fractionDigits = 2;
            }
        }
        number = floorCurrency(number, currency, fractionDigits);
    }
    return (number + "").replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",") + " " + currency.toUpperCase();
}

export function formatPercent(number, fractionDigits = 2) {
    return new BigNumber(number).multipliedBy(100).toFixed(fractionDigits) + "%";
}

export function getToken(token) {
    const tokens = store.getState().Tokens.list;
    if (!token) {
        return null;
    }
    return tokens[token.toLowerCase()];
}

export function formatEpochTime(timestamp) {
    if (!timestamp) {
        return "-";
    }
    return moment(timestamp * 1000).format("YYYY-MM-DD HH:mm:ss");
}

export function formatEpochDate(timestamp) {
    if (!timestamp) {
        return "-";
    }
    return moment(timestamp * 1000).format("YYYY-MM-DD");
}

export function sanitizeObj(obj) {
    const newObj = {};
    Object.keys(obj).forEach(key => {
        if (obj[key] && typeof obj[key] === "object") {
            newObj[key] = sanitizeObj(obj[key]); // recurse
        } else if (obj[key] !== "") {
            newObj[key] = obj[key]; // copy value
        }
    });
    return newObj;
}

export function sanitizeObjForCsv(obj) {
    const newObj = {};
    Object.keys(obj).forEach(key => {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (typeof obj[key] === "string" && dateRegex.test(obj[key])) {
            newObj[key] = moment(obj[key], "YYYY-MM-DD").format("MM/DD/YYYY");
            return;
        }
        if (typeof obj[key] === "string" || typeof obj[key] === "number") {
            newObj[key] = obj[key]; // copy value
        }
    });
    return newObj;
}

export function formatPhoneNumber(phoneNumberString) {
    const cleaned = ("" + phoneNumberString).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return "(" + match[1] + ") " + match[2] + "-" + match[3];
    }
    return null;
}

export function sanitizeNumber(num) {
    if (!num) {
        return num;
    }
    return num.replace(/\D/g, "");
}

export function formatSsn(ssn) {
    if (!ssn) {
        return "";
    }
    let val = sanitizeNumber(ssn).slice(0, 9);
    let newVal = "";
    if ((val.length > 3) && (val.length < 6)) {
        newVal += val.substr(0, 3) + "-";
        val = val.substr(3);
    }
    if (val.length > 5) {
        newVal += val.substr(0, 3) + "-";
        newVal += val.substr(3, 2) + "-";
        val = val.substr(5);
    }
    newVal += val;
    return newVal;
}

export function maskSsn(ssn) {
    let val = sanitizeNumber(ssn);
    if (val && val.length === 9) {
        return "XXX-XX-" + val.slice(-4);
    }
    return ssn;
}

export function formatUsDate(dateStr) {
    const pattern = /(\d{4})-(\d{2})-(\d{2})/;
    if (!dateStr || !dateStr.match(pattern)) {
        return dateStr;
    }
    return dateStr.replace(pattern, "$2/$3/$1");
}

export function toTitleCase(str) {
    return str.replaceAll("_", " ").replace(/\w\S*/g, (txt) => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

export function getDefaultAvatar() {
    return "/" + (store.getState().Variables?.env?.AVATAR || "avatar-hz.png");
}

export function sortObjectByKey(obj, key) {
    return obj.sort((a, b) => (typeof a[key] === "string" ? a[key].localeCompare(b[key]) : a[key] - b[key]));
}

export function getAccountAlias(group) {
    const profile = store.getState()?.User?.profile;
    const environmentOverrides = constants.environmentOverrides[store.getState().Variables?.env?.VARIANT] || {};
    switch (group) {
        case "bd":
            return environmentOverrides.bdAlias || "Asset Manager";
        case "ria":
            return environmentOverrides.riaAlias || "RIA";
        case "agent":
            return environmentOverrides.agentAlias || "Advisor";
        case "client":
            if (profile?.type === "investor token plan") {
                return "Wallet";
            }
            return environmentOverrides.clientAlias || "Client";
        case "account":
            return environmentOverrides.accountAlias || "Accounts";
        case "tokenManagement":
            return environmentOverrides.tmAlias || "Grant Management";
        case "trade":
            return "Transact";
        case "investor token plan":
            return "Multi-Wallet Investor Grantee";
        default:
            return toTitleCase(group);
    }
}

export function getAccountPath(group) {
    const profile = store.getState()?.User?.profile;
    const env = store.getState().Variables?.env;
    const environmentOverrides = constants.environmentOverrides[env?.VARIANT] || {};
    switch (group) {
        case "bd":
            return environmentOverrides.bdPath || "ams";
        case "ria":
            return environmentOverrides.riaPath || "rias";
        case "agent":
            return environmentOverrides.agentPath || "advisors";
        case "client":
            if (profile?.type === "investor token plan") {
                return "wallets";
            }
            return environmentOverrides.clientPath || "clients";
        default:
            return group + "s";
    }
}

export function getPageTitle(pathname) {
    let title = pathname.charAt(1).toUpperCase() + pathname.slice(2);
    switch (pathname) {
        case "/token-grants":
            title = "Grants";
            break;
        case "/tps":
            title = "Plans";
            break;
        case "/oas":
            title = "OAs";
            break;
        case "/token-dashboard":
            title = "Dashboard";
            break;
        default:
    }
    return title + " | " + (store.getState().Variables?.env?.NAME || "HeightZero");
}

export function getDashboardPath() {
    return store.getState().Variables?.env?.VARIANT === "TOKEN_MGMT" ? "/token-dashboard" : "/dashboard";
}

export function flatten(arr) {
    return arr.reduce((flat, toFlatten) => flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten), []);
}

export function updateSheetRange(ws) {
    var range = {s: {r: Infinity, c: Infinity}, e: {r: 0, c: 0}};
    Object.keys(ws).filter(function (x) {
        return x.charAt(0) !== "!";
    }).map(xlsx.utils.decode_cell).forEach(function (x) {
        range.s.c = Math.min(range.s.c, x.c);
        range.s.r = Math.min(range.s.r, x.r);
        range.e.c = Math.max(range.e.c, x.c);
        range.e.r = Math.max(range.e.r, x.r);
    });
    ws["!ref"] = xlsx.utils.encode_range(range);
}

export function getFeeCurrency(tx) {
    switch (tx.action) {
        case "deposit":
        case "yield":
        case "unstake":
        case "unlock":
        case "redelegate":
        case "claim":
            return tx.currencyIn;
        case "withdraw":
        case "charge":
        case "stake":
        case "lock":
        case "sendData":
            return tx.currencyOut;
        default:
            return "usd";
    }
}

export function getTerms() {
    const isTokenManagement = store.getState().Variables?.env?.VARIANT === "TOKEN_MGMT";
    return isTokenManagement ? <a
        target="_blank" rel="noopener noreferrer"
        href="https://content.heightzero.co/terms-of-use-tkmp">Terms
        of Use</a> : <a
        target="_blank" rel="noopener noreferrer"
        href="https://content.heightzero.co/terms-of-service-tdamp">Terms
        of Service</a>;
}

export function parseClientId(clientId) {
    let individualClientId = null;
    let corporateClientId = null;
    if (/^IC\d+$/i.test(clientId)) {
        individualClientId = parseInt(clientId.slice(2));
    } else if (/^\d+$/.test(clientId)) {
        individualClientId = parseInt(clientId);
    } else if (/^CC\d+$/i.test(clientId)) {
        corporateClientId = parseInt(clientId.slice(2));
    }
    return {
        individualClientId,
        corporateClientId
    };
}

export function hexToRgbA(hex, opacity){
    var c;
    if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)){
        c= hex.substring(1).split("");
        if(c.length=== 3){
            c= [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c= "0x"+c.join("");
        return "rgba("+[(c>>16)&255, (c>>8)&255, c&255].join(",")+","+opacity+")";
    }
    throw new Error("Bad Hex");
}

export const isLegacySubdomain = (location) => {
    const subdomains = [
        "zetachain.bitgowealthmanagement.com",
        "ast.bitgowealthmanagement.com",
        "bera.bitgowealthmanagement.com",
        "tdamp.bitgowealthmanagement.com",
        "eigen.bitgowealthmanagement.com",
        "every.bitgowealthmanagement.com",
        "indysquare-tdamp.bitgowealthmanagement.com",
        "lyncster.bitgowealthmanagement.com",
        "layerzero.bitgowealthmanagement.com",
        "mysten.bitgowealthmanagement.com",
        "onrampbtc.bitgowealthmanagement.com",
        "swanbtc.bitgowealthmanagement.com",
        "sei.bitgowealthmanagement.com",
        "tm.bitgowealthmanagement.com",
        "ton.bitgowealthmanagement.com",
        "wct.bitgowealthmanagement.com",
        "worldcoin.bitgowealthmanagement.com",
        "zksync-tmdemo.bitgowealthmanagement.com",
        "tdamp.heightzero.io",
        // "localhost:3000"
    ];
    if (subdomains.includes(location.host)) return true;
    if (subdomains.some((s) => location.host.includes(s))) return true;
    return false;
};
